import { type EntitySubset } from '../entity-subset.js'
import { type Entity } from '../entity.js'
import {
	type ExecutionContext,
	type ExecutionLocation,
} from '../../script/execution-context.js'
import type {
	EntityClass,
	EntityCommon,
	StaticEntityCommon,
} from '../../types.js'
import { BoInfoSymbol } from '../../consts.js'
import { EntityMethod } from '../entity-method.js'
import { type BoReference } from '../bo-reference.js'
import { Parameter } from '../../script/parameter.js'
import { TypeReference } from '../../data/type-reference.js'
import { type EntityProperty } from '../entity-property.js'
import { type StaticEntity } from '../static-entity.js'
import { createCodeBlockWriter } from '../../util/data-util.js'
import { type PropertyChain } from '../../data/data-query-filter.js'
import { type BusinessObject } from '../business-object.js'
export class EntityUtils {
	static getClassNameForSubset(entity: Entity, subset: EntitySubset) {
		return (
			`${entity.moduleId}__${entity.boId}` +
			(subset?.name ? `__${subset.name}` : '')
		)
	}

	static getUserFriendlyClassNameForSubset(
		entity: Entity,
		subset: EntitySubset,
	) {
		return subset.name || entity.boId
	}

	static getQualifiedClassNameForSubset(entity: Entity, subset: EntitySubset) {
		return entity.getQualifiedName() + (subset.name ? `.${subset.name}` : '')
	}

	static getMethodsForSubset(
		entity: Entity,
		subset: EntitySubset,
		executionContext: ExecutionContext,
	) {
		const ctor = entity.methods.find(
			(m) => m.name == 'constructor' && !subset.name,
		)
		const available = entity.methods.filter((m) => {
			if (m.name == 'constructor') return false
			if (subset.itemAvailability.get(m.name) != 'y') return false

			return executionContext.isEntityMethodAvailable(m)
		})

		const getters = available.filter((m) => m.availability == 'calculation')
		const normalMethods = available.filter(
			(m) => m.availability != 'calculation',
		)

		return { ctor, getters, normalMethods }
	}

	static getEffectiveMethodInputs(
		method: EntityMethod,
		boRefs: BoReference[],
		entity: Entity,
		executionLocation: ExecutionLocation,
	) {
		let inputs = [...method.inputs]

		if (method.availability == 'callableFromClient' && !method.isStatic) {
			if (executionLocation == 'server') {
				inputs.splice(
					0,
					0,
					new Parameter({
						name: 'clientThis',
						type: entity.getQualifiedName(),
						defaultExpression: '',
					}),
				)
			}
			if (executionLocation == 'client') {
				const dataStoreRefs = boRefs.filter(
					(ref) => ref.boType == 'ServerDataStore',
				)
				inputs.splice(
					0,
					0,
					new Parameter({
						name: 'serverStore',
						type: [
							...dataStoreRefs.map(
								(ref) => `typeof ${ref.moduleId}.${ref.boId}`,
							),
							'null',
						].join(' | '),
						defaultExpression: '',
					}),
				)
			}
		}

		return inputs
	}

	static calculateTsDeclarations(
		entity: Entity,
		boRefs: BoReference[],
		executionContext: ExecutionContext,
		getStaticEntity: (typeRef: TypeReference) => StaticEntity,
	): string[] {
		const defs: string[] = []

		defs.push(
			this.calculateTsDeclarationsForSubset(
				entity,
				entity.getMainSubset(),
				boRefs,
				executionContext,
				getStaticEntity,
			),
		)

		const subsetDefs = entity.subsets
			.map((subset) =>
				this.calculateTsDeclarationsForSubset(
					entity,
					subset,
					boRefs,
					executionContext,
					getStaticEntity,
				),
			)
			.join('\n')

		const typeRef = new TypeReference(entity.idType)
		defs.push(`export namespace ${entity.boId} {
			${
				entity.idType != 'none'
					? `export type Id = NominalType<${entity.idType}, '${entity.getQualifiedName()}'>
				export const Id: typeof ${typeRef.getConstructorName()}`
					: ''
			}
			${subsetDefs}
		}`)

		return defs
	}

	private static calculateTsDeclarationsForSubset(
		entity: Entity,
		subset: EntitySubset,
		boRefs: BoReference[],
		executionContext: ExecutionContext,
		getStaticEntity: (typeRef: TypeReference) => StaticEntity,
	): string {
		const className = this.getUserFriendlyClassNameForSubset(entity, subset)
		const availableProperties = [
			...entity.getAvailablePropertiesForSubset(subset),
		]
		const methods = this.getMethodsForSubset(entity, subset, executionContext)
		const staticEntitiesForProperties = this.getBosForProperties(
			entity,
			getStaticEntity,
		)

		const writer = createCodeBlockWriter()
		const maxLenWriter = createCodeBlockWriter()
		const mandatoryWriter = createCodeBlockWriter()

		writer.write(`export class ${className}`).block(() => {
			// writer.writeLine(`static readonly θpropertyNames: [${this.getPropertyNames(entity, availableProperties, getStaticEntity).join(', ')}]`)
			// writer.writeLine(`static readonly θsubsetNames: ${JSON.stringify(entity.subsets.map(s => s.name))}`)
			writer.writeLine(`get __type(): string`)
			writer.writeLine(`get __baseType(): string`)
			writer.writeLine(`static get __type(): string`)
			writer.writeLine(`static get __baseType(): string`)
			if (entity.idType != 'none') {
				writer.writeLine(`id: ${entity.boId}.Id`)
			}
			availableProperties.forEach((p) => {
				const staticEntity = staticEntitiesForProperties.get(p.name)

				writer.writeLine(`${p.isStatic ? 'static ' : ''}${p.name}: ${p.type}`)
				if (staticEntity) {
					writer.writeLine(
						`${p.isStatic ? 'static ' : ''}'${p.name}$id': ${staticEntity.idType}`,
					)
				}

				if (!p.isStatic) {
					maxLenWriter.writeLine(`${p.name}: number,`)
					mandatoryWriter.writeLine(`${p.name}(): boolean,`)
				}
			})

			const entityInfoType = `EntityInfo & {
				allPropertyNames: [${this.getPropertyNames(entity, availableProperties, getStaticEntity).join(', ')}]
			}`

			// writer.write('static θmaxLength: ').block(() => writer.write(maxLenWriter.toString()))
			// writer.write('θmaxLength: ').block(() => writer.write(maxLenWriter.toString()))
			// writer.write('θisMandatory: ').block(() => writer.write(mandatoryWriter.toString()))
			writer.writeLine(`static [BoInfoSymbol]: ${entityInfoType}`)
			writer.writeLine(`[BoInfoSymbol]: ${entityInfoType}`)

			if (methods.ctor) {
				writer.writeLine(methods.ctor.getSignature(executionContext, true))
			} else {
				writer.writeLine(
					`constructor(init?: Partial<${className}>, initOneToManyRelations?: boolean)`,
				)
			}
			methods.getters.forEach((m) =>
				writer.writeLine(
					`${m.isStatic ? 'static ' : ''}get ${m.name}(): ${m.returnType}`,
				),
			)
			methods.normalMethods.forEach((m) => {
				const methodClone = new EntityMethod({
					...m,
					inputs: this.getEffectiveMethodInputs(
						m,
						boRefs,
						entity,
						executionContext.getExecutionLocation(),
					),
				})
				writer.writeLine(methodClone.getSignature(executionContext, true))
			})
			// writer.writeLine(`static $initNewInstance(): ${className}`)
			writer.writeLine(
				`θtoPlainObject(recursive?: boolean, includeType?: boolean): ${className}`,
			)
			writer.writeLine(`$clone(): ${className}`)
			writer.writeLine(`$updateFrom(source: Partial<${className}>): void`)
		})

		return writer.toString()
	}

	static getBosForProperties<T extends BusinessObject>(
		entity: Entity,
		getBo: (typeRef: TypeReference) => T,
	) {
		const map: Map<string, T> = new Map()

		for (const property of entity.getPropertiesForPropertyTree()) {
			const typeRef = new TypeReference(property.type).forModule(entity)

			if (typeRef.kind != 'bo' || typeRef.isArrayOrSet) continue
			if (!typeRef.boModuleId || !typeRef.boId) continue

			const bo = getBo?.(typeRef)
			if (!bo) continue

			map.set(property.name, bo)
		}
		return map
	}

	static getPropertyNames(
		entity: Entity,
		availableProperties: EntityProperty[],
		getStaticEntity: (typeRef: TypeReference) => StaticEntity,
	) {
		const staticEntitiesForProperties = this.getBosForProperties(
			entity,
			getStaticEntity,
		)
		const propertyNames: string[] = availableProperties.flatMap((p) => {
			if (staticEntitiesForProperties.has(p.name)) {
				const n = [`'${p.name}$id'`]
				if (staticEntitiesForProperties.get(p.name)!.hasOtherEntries) {
					n.push(`'${p.name}$other'`)
				}
				return n
			}
			return [`'${p.name}'`]
		})

		if (entity.idType != 'none') propertyNames.splice(0, 0, `'id'`)

		return propertyNames
	}

	static getNestedPropertyInfo(
		obj: EntityCommon,
		path: string,
		allClasses: Record<string, Record<string, any>>,
	): PropertyChain | null {
		const parts = path.split('.')
		let chain: string[] = []
		let propertyType = ''
		let propertyKindType: 'primitive' | 'entity' | 'staticEntity' = 'primitive'

		for (const [idx, part] of parts.entries()) {
			const availableProperties = obj[BoInfoSymbol].allPropertyNames
			if (obj[BoInfoSymbol].staticEntityProperties.includes(part)) {
				propertyKindType = 'staticEntity'
			}
			let foundProperty = findNearest(part, availableProperties)
			if (!foundProperty) {
				foundProperty = part
			} else {
				const combinedProperty = parts.slice(idx, idx + 2).join('.') // things like 'gender.id'
				foundProperty = findNearest(combinedProperty, availableProperties)
			}

			if (foundProperty) {
				chain.push(foundProperty)
				propertyType = obj[BoInfoSymbol].propertyTypes[foundProperty]
				obj = obj[
					foundProperty as keyof EntityCommon
				] as unknown as EntityCommon
				break
			} else {
				const availableRelations = {
					// ...obj?.[BoInfoSymbol]?.oneToManyRelationships, // don't use one-to-many here!
					...obj?.[BoInfoSymbol]?.manyToOneRelationships,
					// TODO: add oneToOne when implemented
				}
				const foundRelation = findNearest(part, Object.keys(availableRelations))
				const relationType = foundRelation
					? availableRelations[foundRelation]?.()?.__baseType
					: null
				if (relationType && foundRelation) {
					chain.push(foundRelation)
					const [moduleId, boId] = relationType.split('.')
					obj = allClasses[moduleId]?.[boId]
					propertyKindType = 'entity'
				} else if ((obj as any)?.[BoInfoSymbol]?.boType == 'StaticEntity') {
					propertyType = (obj as unknown as StaticEntityCommon).__type
					propertyKindType = 'staticEntity'
					break
				} else if (
					(obj as EntityClass)?.[BoInfoSymbol]?.embeddedEntities?.[parts[0]]
				) {
					chain = parts
					propertyType = (obj as unknown as EntityCommon).__type
					propertyKindType = 'entity'
					break
				}
			}
		}

		if (!chain.length) return null
		if (chain.length < parts.length) {
			chain.push(parts.slice(chain.length).join('.')) // add remaining "unmatched" parts (e.g. ['gender', 'id']) as a final property joined by dots ('gender.id')
		}
		return {
			chain,
			propertyType,
			propertyKindType,
		}

		function findNearest(name: string, names: readonly string[]) {
			if (names.includes(name)) return name
			const nearest = names.find((n) => n.toLowerCase() == name.toLowerCase())
			return nearest
		}
	}
}
